<script setup lang="ts">

import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from 'vue-router';
import {Itinerary} from "@/models/Itinerary";
import { useI18n } from 'vue-i18n';
import CustomButton from "@/components/Button/Button.vue";
import { useHead } from '@vueuse/head'
import ItineraryList from "@/components/Itineraries/ItineraryList.vue";
import {useItineraryStore} from "@/stores/itineraryStore";
import {VueSpinner} from "vue3-spinners";
import MapboxMap from "@/components/Map/MapboxMap.vue";
import defaultImage from '@/assets/images/default-itinerary.jpeg'
import {useFormatStore} from "@/stores/formatStore";
import {useLocalization} from "@/locales/localization";
import { getAsset } from "@/helpers/assetsUtils";


const route = useRoute();
const router = useRouter();
const formatStore = useFormatStore();
const loading = ref(false);
const itinerary = ref<Itinerary>({});
const { t } = useI18n();
const itineraryStore = useItineraryStore();
const { currentLocale } = useLocalization();


const loadItinerary = async () => {
  loading.value = true;

  const slug = route.params.slug as string;
  const uuid = itineraryStore.getItineraryBySlug(slug);

  if (!uuid) {
    router.push({ path: '/notfound' });
    return;
  }

  try {
    const data = await itineraryStore.getItinerary(uuid);
    itinerary.value = data.data;

    useHead({
      title: itinerary.value.seoTitle || itinerary.value.title,
      meta: [
        { name: 'description', content: itinerary.value.seoDescription || '' },
        { property: 'og:title', content: itinerary.value.seoTitle || itinerary.value.title },
        { property: 'og:description', content: itinerary.value.seoDescription || '' },
        { property: 'og:image', content: itinerary.value.imageSet?.['760w'] || '' },
        { property: 'og:type', content: 'article' }
      ]
    });
  } catch {
    router.push({ path: '/notfound' });
  } finally {
    loading.value = false;
  }
}



// Ordered list of stops
const stops = computed(() => {
  if (!itinerary.value.steps || itinerary.value.steps.length === 0) return []

  const arr: any[] = []

  for (let i = 0; i < itinerary.value.steps.length; i++) {
    const dest = itinerary.value.steps[i]
    if (dest?.from && dest?.to) {
      arr.push({
        name: dest.from.title,
        to: dest.to.title,
        nauticalMiles: dest.nauticalMiles,
        rendered: dest.rendered, 
        latitude: dest.from.latitude,
        longitude: dest.from.longitude
      })
    }
  }

  // Add final stop (last 'to')
  const last = itinerary.value.steps[itinerary.value.steps.length - 1]
  if (last?.to) {
    arr.push({
      name: last.to.title,
      latitude: last.to.latitude,
      longitude: last.to.longitude
    })
  }

  return arr
})

// track the currently expanded stop (-1 or null means none)
const expandedStop = ref<number | null>(null)
const expandedRefs = ref<any[]>([]);

const toggleStop = async (i: number) => {
  expandedStop.value = expandedStop.value === i ? null : i;
  await nextTick();
  if (expandedStop.value !== null) {
    const el = expandedRefs.value[expandedStop.value];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
};

// compute the last expanded index (for line styling)
const lastExpandedIndex = computed(() => {
  return expandedStop.value !== null ? expandedStop.value : -1
})


watch(route, () => {
  loadItinerary();
});
onMounted(async() => {
  loading.value = true;
  
  if (!itineraryStore.hasItineraries) {
    await itineraryStore.getAll(1, 100, 'cosmosy', '', null, { splitHero: false }); // load all or enough pages
  }
  await loadItinerary();
  loading.value = false;
});

watch(currentLocale, () => {
  loadItinerary();
})

</script>

<template>

  <div class="flex flex-col" v-if="!loading">
    <div
        :style="{
            backgroundImage:
            'url(' + (itinerary?.imageSet?.max || defaultImage) + ')'
        }"
        class="main-image flex justify-end"
    >
      <div class="container bg-image flex flex-col items-center">
        <div class="news-content flex flex-col w-3/4 gap-4 mb-8">
          <h1 class="drop-shadow self-start responsive-title-2 text-white font-bold">
            {{ itinerary?.title }}
          </h1>
        </div>
      </div>
    </div>


    <div class="container mt-8 flex flex-col items-center">
      <div class="news-content flex flex-col w-3/4 gap-4">
        <div class="flex flex-col gap-2">
          <h3 v-if="itinerary?.publishedAt" class="text-bw-darkGrey lg:responsive-body-small self-start">
            {{ formatStore.formatDateWithCustomPatternMapping(itinerary?.publishedAt, 'MMM DD, YYYY') }}
          </h3>
        </div>
        <div v-if="itinerary.steps && itinerary.steps.length > 0" class="grid grid-cols-1 sm:grid-cols-3 gap-12"> 

          <!-- Left column -->
          <div class="sm:col-span-1">
            <div v-if="itinerary.nauticalMiles" class="mb-4 responsive-title-6 font-semibold">
              Total Distance: {{ itinerary.nauticalMiles }} NM
            </div>
            <ol class="relative border-s-[1px] border-dashed border-black mt-8">
              <li v-for="(stop, i) in stops" :key="i" class="mb-10 ms-4">
                <!-- custom point -->
                <div
                    class="absolute w-3 h-3 rounded-full mt-1.5 -start-1.5 border border-white"
                    :class="{
                      'bg-black': i != lastExpandedIndex, 
                      'bg-primary-blue': i == lastExpandedIndex      
                    }"
                ></div>

                <!-- clickable stop name -->
                <div
                    class="responsive-title-6 font-bold cursor-pointer text-black"
                    @click="toggleStop(i)"
                >
                  {{ stop.name }}
                </div>
                <transition name="expand">
                  <div
                      v-if="expandedStop === i && stop.to"
                      ref="expandedRefs[i]"
                      class="mt-2 flex flex-col gap-2 responsive-body-medium"
                  >
                    <div class="flex items-baseline text-black space-x-4">
                      <span class="font-semibold leading-none">{{ stop.to }}</span>
                      <span class="bg-primary-blue rounded-full px-3 py-1 text-white font-semibold responsive-body-xsmall leading-none">
                        ({{ stop.nauticalMiles }} NM)
                      </span>
                    </div>

                    <span class="text-black responsive-body-small" v-html="stop.rendered"></span>
                  </div>


                </transition>

              </li>
            </ol>
            
          </div>

          <!-- Right column -->
          <div class="sm:col-span-2 space-y-6 responsive-body-large text-black mix-blend-multiply">
            <!-- Multiple area pills -->
            <div v-if="itinerary.area && itinerary.area.length" class="flex flex-wrap gap-2">
              <div
                  v-for="ar in itinerary.area"
                  :key="ar.id"
                  class="bg-primary-blue/10 text-primary-blue rounded-full px-3 py-1 text-xs md:text-sm font-medium"
              >
                {{ ar.title }}
              </div>
            </div>
            <div v-html="itinerary.rendered" />
            <MapboxMap
                :stops="stops"
                :highlight-index="expandedStop"
            />
          </div>
        </div>
      </div>

      <div class="w-full travel-inspirations">
        <div class="pt-4 pb-24">
          <CustomButton
              type="outline-black"
              :outlined="true"
              :rounded="true"
              :icon="getAsset('icons', 'Arrow_left_16x16.svg')"
              :hoverIcon="getAsset('icons', 'Arrow_left_white_16x16.svg')"
              @click="router.go(-1)"
          >
            {{ t('goback') }}
          </CustomButton>
        </div>
        <ItineraryList/>
      </div>
    </div>
  </div>
  <div class="flex justify-center items-center min-h-screen" v-else>
    <VueSpinner size="50" class="p-2" color="#1B84FF"
    />
  </div>
</template>

<style scoped>

.main-image {
  background-size: cover;
  background-position: center;
  max-height: 650px;
  height: 65vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  gap: 40px;
}

@media (max-width: 1535px) {
  .bp-image-widget {
    height: 25vh;
  }
}

@media (max-width: 1280px) {
  .bp-image-widget {
    height: 18vh;
  }
}

@media (max-width: 1024px) {

  .news-content {
    width: 100%;
  }
}

:deep(.title-pagelist){
  font-size: 1.25rem!important;
}


</style>